import * as XLSX from 'xlsx'
import type { InstalledAsset } from '@/types/asset'
import { SimplePDFGenerator } from './pdfGenerator'
import { formatAssetDate, formatWarrantyDuration, getWarrantyStatusLabel, getInstallationStatusLabel } from './assetUtils'

// Full column set — used for CSV/Excel where width isn't a constraint.
const toExportRow = (asset: InstalledAsset) => ({
  'Customer Name': asset.customerName,
  Organization: asset.organization,
  'Tender Reference Number': asset.referenceNumber,
  'Tender ID': asset.t247Id,
  'Item Name': asset.itemName,
  'Serial Number': asset.serialNumber,
  Warranty: formatWarrantyDuration(asset.warrantyYears, asset.warrantyMonths),
  'Warranty Expiry Date': formatAssetDate(asset.warrantyExpiry),
  'Warranty Remaining': asset.remainingDays === null ? 'N/A' : `${asset.remainingDays} day(s)`,
  'Warranty Status': getWarrantyStatusLabel(asset.warrantyStatus),
  'Installation Status': getInstallationStatusLabel(asset.installationStatus),
  Engineer: asset.engineer,
  Consignee: asset.consignee,
  'Installation Date': formatAssetDate(asset.installationDate),
  Remarks: asset.remarks,
  'Report Created Date': formatAssetDate(asset.reportDate)
})

const buildWorksheet = (assets: InstalledAsset[]) => {
  const rows = assets.map(toExportRow)
  return XLSX.utils.json_to_sheet(rows)
}

const timestampedFilename = (base: string, ext: string): string =>
  `${base}_${new Date().toISOString().split('T')[0]}.${ext}`

export const exportAssetsToExcel = (assets: InstalledAsset[]): void => {
  const worksheet = buildWorksheet(assets)
  const workbook = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Installed Assets')
  XLSX.writeFile(workbook, timestampedFilename('Installed_Assets', 'xlsx'))
}

export const exportAssetsToCsv = (assets: InstalledAsset[]): void => {
  const worksheet = buildWorksheet(assets)
  const workbook = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Installed Assets')
  XLSX.writeFile(workbook, timestampedFilename('Installed_Assets', 'csv'), { bookType: 'csv' })
}

// Compact column set for PDF — a full 16-column table renders unreadably
// small once rasterized into an A4 page, so PDF export focuses on the
// columns most useful for a printed handout.
export const exportAssetsToPdf = async (assets: InstalledAsset[]): Promise<void> => {
  const rows = assets
    .map(
      asset => `
        <tr>
          <td>${asset.customerName || '-'}</td>
          <td>${asset.itemName || '-'}</td>
          <td>${asset.serialNumber || '-'}</td>
          <td>${formatAssetDate(asset.warrantyExpiry)}</td>
          <td>${getWarrantyStatusLabel(asset.warrantyStatus)}</td>
          <td>${getInstallationStatusLabel(asset.installationStatus)}</td>
          <td>${asset.engineer || '-'}</td>
        </tr>`
    )
    .join('')

  const htmlContent = `
    <div style="font-family: Arial, sans-serif;">
      <h2 style="margin-bottom: 4px;">Installed Assets</h2>
      <p style="margin-top: 0; color: #555; font-size: 12px;">
        Generated ${new Date().toLocaleString('en-IN')} — ${assets.length} asset(s)
      </p>
      <table style="width: 100%; border-collapse: collapse; font-size: 11px;">
        <thead>
          <tr style="background: #f3f4f6; text-align: left;">
            <th style="border: 1px solid #d1d5db; padding: 6px;">Customer</th>
            <th style="border: 1px solid #d1d5db; padding: 6px;">Item Name</th>
            <th style="border: 1px solid #d1d5db; padding: 6px;">Serial Number</th>
            <th style="border: 1px solid #d1d5db; padding: 6px;">Warranty Expiry</th>
            <th style="border: 1px solid #d1d5db; padding: 6px;">Warranty Status</th>
            <th style="border: 1px solid #d1d5db; padding: 6px;">Installation</th>
            <th style="border: 1px solid #d1d5db; padding: 6px;">Engineer</th>
          </tr>
        </thead>
        <tbody>${rows}</tbody>
      </table>
    </div>
  `

  const style = document.createElement('style')
  style.id = 'asset-export-pdf-style'
  style.textContent = 'td, th { border: 1px solid #d1d5db; padding: 6px; }'
  document.head.appendChild(style)
  try {
    await SimplePDFGenerator.generatePDF(htmlContent, timestampedFilename('Installed_Assets', 'pdf'))
  } finally {
    style.remove()
  }
}
